import { useNavigate } from 'react-router-dom';
import { Community } from '@/mocks/communities';
import GlowButton from '@/components/base/GlowButton';
import { useScrollReveal } from '@/hooks/useScrollReveal';

interface CommunityCardProps {
  community: Community;
}

const activityConfig = {
  high: { label: 'High Activity', color: 'text-emerald-600', dot: 'bg-emerald-500' },
  medium: { label: 'Medium Activity', color: 'text-amber-600', dot: 'bg-amber-500' },
  low: { label: 'Low Activity', color: 'text-slate-400', dot: 'bg-slate-400' },
};

export default function CommunityCard({ community }: CommunityCardProps) {
  const navigate = useNavigate();
  const cardRef = useScrollReveal<HTMLElement>({ threshold: 0.08, delay: 0 });
  const activity = activityConfig[community.activityLevel];
  const formatCount = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return n.toString();
  };
  const formatDate = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  return (
    <article
      ref={cardRef}
      onClick={() => navigate(`/community/${community.id}`)}
      className="group relative bg-white/55 backdrop-blur-md rounded-2xl border border-slate-200/80 overflow-hidden flex flex-col cursor-pointer"
      style={{
        transition: 'transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease, border-color 0.3s ease',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget;
        el.style.transform = 'translateY(-4px) scale(1.01)';
        el.style.background = 'linear-gradient(135deg, rgba(0,168,240,0.05) 0%, rgba(200,230,248,0.18) 100%)';
        el.style.backdropFilter = 'blur(16px) saturate(140%)';
        el.style.WebkitBackdropFilter = 'blur(16px) saturate(140%)';
        el.style.borderColor = 'rgba(0,136,204,0.18)';
        el.style.boxShadow = '0 8px 24px rgba(0,136,204,0.07), 0 2px 6px rgba(0,136,204,0.05), inset 0 1px 0 rgba(255,255,255,0.60)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget;
        el.style.transform = '';
        el.style.background = '';
        el.style.backdropFilter = '';
        el.style.WebkitBackdropFilter = '';
        el.style.borderColor = '';
        el.style.boxShadow = '';
      }}
    >
      {/* 顶部折射高光线 — hover 时显现 */}
      <span
        aria-hidden="true"
        className="absolute top-0 left-[10%] right-[10%] h-px pointer-events-none z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.60), transparent)' }}
      />

      {/* Cover Image */}
      <div className="relative w-full h-36 overflow-hidden shrink-0">
        <img
          src={community.coverImage}
          alt={community.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent group-hover:from-[#c8e6f8]/50 transition-all duration-300"></div>
        {/* Member count badge */}
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 bg-white/75 backdrop-blur-sm px-2.5 py-1 rounded-full border border-white/60">
          <i className="ri-group-line text-[#0088CC] text-xs w-3 h-3 flex items-center justify-center"></i>
          <span className="text-slate-700 text-xs font-semibold">{formatCount(community.memberCount)}</span>
        </div>
        {/* Featured badge */}
        {community.featured && (
          <div className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-[#0088CC]/90 backdrop-blur-sm px-2 py-0.5 rounded-full">
            <i className="ri-star-fill text-white text-[10px] w-3 h-3 flex items-center justify-center"></i>
            <span className="text-white text-[10px] font-semibold">Featured</span>
          </div>
        )}
      </div>

      {/* Card Body */}
      <div className="p-4 flex flex-col flex-1">
        {/* Category tag */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-semibold text-[#0088CC] bg-[#0088CC]/10 px-2 py-0.5 rounded-full uppercase tracking-wider">
            {community.category}
          </span>
          <span className="text-[10px] text-slate-400">{community.region}</span>
        </div>

        {/* Name */}
        <h3 className="text-base font-bold text-slate-800 leading-snug mb-1.5 line-clamp-2 group-hover:text-[#0088CC] transition-colors">
          {community.name}
        </h3>

        {/* Description */}
        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 flex-1 mb-3">
          {community.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {community.tags.map((tag) => (
            <button
              key={tag}
              onClick={(e) => { e.stopPropagation(); navigate(`/tag/${tag}`); }}
              className="text-[10px] text-slate-400 hover:text-[#0088CC] bg-slate-100/80 hover:bg-[#0088CC]/10 border border-slate-200/60 hover:border-[#0088CC]/30 px-2 py-0.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              #{tag}
            </button>
          ))}
        </div>

        {/* Meta */}
        <div className="flex items-center gap-3 text-[11px] text-slate-400 mb-3">
          <div className="flex items-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-full ${activity.dot}`}></span>
            <span className={activity.color}>{activity.label}</span>
          </div>
          <span className="text-slate-300">|</span>
          <div className="flex items-center gap-1">
            <i className="ri-calendar-line text-[10px] w-3 h-3 flex items-center justify-center"></i>
            <span>{formatDate(community.createdAt)}</span>
          </div>
        </div>

        {/* Join Button */}
        <GlowButton
          as="a"
          href={community.telegramLink}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="w-full text-sm py-2.5 rounded-xl"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
        >
          <i className="ri-telegram-fill text-base"></i>
          Join Community
        </GlowButton>
      </div>
    </article>
  );
}
